import { useRef, useState } from "react";
import { Upload, Loader2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

export const MultiImageUploader = ({ value = [], onChange, testid = "multi-image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");

  const uploadAll = async (files) => {
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed: ${file.name}`);
      }
    }
    if (added.length) {
      onChange([...(value || []), ...added]);
      toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} added`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadAll(files);
  };

  const addUrl = () => {
    const v = url.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setUrl("");
  };

  const removeAt = (i) => onChange((value || []).filter((_, j) => j !== i));

  return (
    <div data-testid={testid}>
      {(value || []).length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((img, i) => (
            <div key={i} className="group relative h-16 w-24 overflow-hidden rounded-lg border border-gray-200">
              <img src={img} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => removeAt(i)} data-testid={`${testid}-remove-${i}`}
                className="absolute right-0 top-0 rounded-bl bg-brandred p-0.5 text-white opacity-90 hover:opacity-100">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-btn`}
          className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy-700 disabled:opacity-60">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload photos"}
        </button>
        <div className="flex flex-1 items-center gap-1.5">
          <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
            placeholder="…or paste an image URL" data-testid={`${testid}-url`}
            className="min-w-0 flex-1 rounded-full border border-black/10 bg-white px-3.5 py-2 text-xs text-navy outline-none focus:border-navy" />
          <button type="button" onClick={addUrl} className="rounded-full border border-navy/20 p-2 text-navy hover:bg-navy-50"><Plus size={14} /></button>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple onChange={handleFiles} className="hidden" data-testid={`${testid}-input`} />
    </div>
  );
};
